import assert from "assert";
import {
  type FC,
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useKey } from "react-use";
import classNames from "classnames";
import FocusLock from "react-focus-lock";
import { exceptionMessage } from "@/utils/utils";
import { Close } from "../icons";
import Backdrop from "./Backdrop";
import Card from "./Card";
import styles from "./Modal.module.css";
import Button from "./Button";

export interface Props {
  children?: ReactNode;
  agree?: string;
  cancel?: string | null;
  hideModal: () => void;
  loadingMessage?: string;
  onAgree?: () => unknown;
  onCancel?: () => unknown;
  onClose?: () => unknown;
  onBeforeAgree?: () => boolean | Promise<boolean>;
  onSuccess?: () => void;
  title: string;
  subtitle?: React.ReactNode;
  successMessage?: string;
  size?: "small" | "medium" | "large" | "full" | "custom";
  customBackdrop?: string;
  className?: string;
  disabled?: boolean;
  formClassName?: string;
  canBeClosed?: boolean;
  noCancel?: boolean;
  formContainer?: boolean;
  modalBodyClassname?: string;
  header?: (close: () => void, defaultHeader: ReactNode) => ReactNode;
  footer?: ReactNode;
  zIndex?: number;
}

const Modal: FC<Props> = ({
  agree,
  cancel = "Cancel",
  children,
  disabled,
  hideModal,
  loadingMessage = "Loading",
  onAgree,
  onCancel,
  onClose,
  onBeforeAgree,
  onSuccess,
  title,
  subtitle,
  successMessage = "Success!",
  size = "medium",
  customBackdrop,
  className,
  modalBodyClassname,
  formClassName = "",
  canBeClosed = true,
  noCancel = false,
  formContainer = true,
  footer,
  header,
  zIndex = 200000,
}) => {
  const portalRef = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const submit = async (event?: FormEvent<HTMLFormElement> | KeyboardEvent) => {
    event?.preventDefault();
    if (onBeforeAgree) {
      if (!(await onBeforeAgree())) return;
    }
    if (!onAgree) return;
    const loadingId = toast.loading(loadingMessage);
    try {
      setIsLoading(true);
      await onAgree();
      toast.dismiss(loadingId);
      onSuccess?.();
      if (successMessage) {
        toast.success(successMessage);
      }
      closeModal();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.dismiss(loadingId);
      toast.error(exceptionMessage(error, "Failed"));
    }
  };

  const closeModal = () => {
    if (!canBeClosed) return;
    onClose?.();
    hideModal();
  };

  useKey("Escape", closeModal);

  useEffect(() => {
    portalRef.current = document.getElementById("modal-portal");
    setMounted(true);
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const { small, medium, large, full } = styles;
  assert(small && medium && large && full);

  const shouldRenderFooter = !!footer || !!agree || (!noCancel && !!cancel);

  const renderCancelButton = () =>
    !noCancel && (
      <Button
        type={"button"}
        variant={"secondary"}
        disabled={isLoading}
        className={styles.cancel}
        onClick={() => {
          onCancel?.();
          closeModal();
        }}
      >
        {cancel ?? "Cancel"}
      </Button>
    );

  const renderAgreeButton = () =>
    agree && (
      <Button
        type={formContainer ? "submit" : "button"}
        onClick={!formContainer ? () => !isLoading && submit() : undefined}
        disabled={disabled || isLoading}
        loading={isLoading}
        className={styles.agree}
      >
        {agree}
      </Button>
    );

  const defaultHeader = (
    <>
      {size === "full" && (
        <div
          className="flex gap-ds-2 text-gray-400 items-center cursor-pointer w-fit mb-ds-4 text-xl hover:underline"
          onClick={closeModal}
        >
          <Close />
          <span>Close</span>
        </div>
      )}
      <div className={styles.header}>
        {size !== "full" ? (
          <>
            <div className="flex flex-col gap-ds-2">
              <h3 className={styles.title}>{title}</h3>
              {subtitle && <span>{subtitle}</span>}
            </div>
            {canBeClosed && (
              <Close className="cursor-pointer" onClick={closeModal} />
            )}
          </>
        ) : (
          <h1 className={styles.title}>{title}</h1>
        )}
      </div>
    </>
  );

  const modalContent = (
    <>
      {header ? header(closeModal, defaultHeader) : defaultHeader}
      <div className={classNames(styles.body, modalBodyClassname)}>
        {children}
      </div>
      {shouldRenderFooter && (
        <div className={styles.footer}>
          {footer ? (
            footer
          ) : size === "full" ? (
            <>
              {renderAgreeButton()}
              {renderCancelButton()}
            </>
          ) : (
            <>
              {renderCancelButton()}
              {renderAgreeButton()}
            </>
          )}
        </div>
      )}
    </>
  );

  return mounted && portalRef.current
    ? createPortal(
        <div
          className="max-h-screen overflow-y-auto"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Backdrop
            className={
              customBackdrop ? styles[customBackdrop] : styles.backdrop
            }
            onClick={closeModal}
            style={{ zIndex }}
          />
          <Card
            elevation={customBackdrop ? "none" : "2"}
            style={{ zIndex }}
            className={classNames(styles.container, className, {
              "max-h-[97vh]": size !== "full",
              [small]: size === "small",
              [medium]: size === "medium",
              [large]: size === "large",
              [full]: size === "full",
            })}
          >
            <FocusLock returnFocus={true} className={"h-full"}>
              {formContainer ? (
                <form
                  className={classNames(styles.contentContainer, formClassName)}
                  onSubmit={submit}
                >
                  {modalContent}
                </form>
              ) : (
                <div
                  className={classNames(styles.contentContainer, formClassName)}
                >
                  {modalContent}
                </div>
              )}
            </FocusLock>
          </Card>
        </div>,
        portalRef.current
      )
    : null;
};

export default Modal;
